import type { StorefrontProduct } from '../../api/client';
import { isColourOption, swatchColours, swatchName } from './colorSwatch';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, numberProp, textProp } from './elementProps';
import { useProduct, useProductVariant } from './productContext';
import { productOptions, variantWithValue } from './purchaseChoice';
import { orderable } from '../../commerce/variantStock';
import './elementSwatches.css';
import { named, useShopNames } from '../../commerce/shopNames';

// A product's choices on its card. As plain dots they only describe the product, as Shopify's Modular prints them. A
// theme may make them act: pressing a colour switches the card to it where it stands, its picture, price and link
// following, and sizes may be drawn as small links to the product on that size.

/** One swatch: the value as the merchant wrote it, and the colour it stands for. */
export type Swatch = { value: string; colour: string };

/** What the builder shows on a card with no product yet, so the row can be placed and styled. */
const SAMPLE: readonly Swatch[] = ['#c0603c', '#efe6d2', '#f2d4dd', '#6e8f66'].map((colour) => ({ value: colour, colour }));

/** The shapes a pressable colour may take. */
const SHAPES = ['round', 'square'] as const;

// Reads a product's colour option as swatches, or none when it has no colour option or a value with no known colour.
export function productSwatches(product: Pick<StorefrontProduct, 'options'> | null): Swatch[] {
  const option = product?.options?.find((entry) => isColourOption(entry.name));
  const colours = option ? swatchColours(option.values) : null;
  return option && colours ? option.values.map((value) => ({ value, colour: colours.get(value) ?? '' })) : [];
}

/** What the pressable colours are drawn from: the element's box, its product, the colours shown, how many more there are and their shape. */
type PickerProps = { box: ReturnType<typeof useElementBox>; product: StorefrontProduct; swatches: readonly Swatch[]; more: number; shape: (typeof SHAPES)[number] };

// Draws the colours as buttons that switch the card to one, then any others as a link to the product's page.
function SwatchPicker({ box, product, swatches, more, shape }: PickerProps) {
  const { selected, select } = useProductVariant();
  const said = useShopNames();
  const { store } = useElementRender();
  const current = product.variants.find((variant) => variant.id === selected) ?? product.variants[0];
  const index = productOptions(product).findIndex((option) => isColourOption(option.name));
  // Says which colour a variant holds, whichever of its two option columns carries it.
  const held = (index === 1 ? current?.option2 : current?.option1) ?? '';
  return (
    <div {...box} className={[box.className, 'xe-swatches', 'xe-swatches-pick'].filter(Boolean).join(' ')} data-shape={shape} role="group" aria-label={named(said.colours, { names: swatches.map((swatch) => swatchName(swatch.value)).join(', ') })}>
      {swatches.map((swatch) => (
        <button key={swatch.value} type="button" className="xe-swatch-pick" aria-pressed={swatch.value === held} aria-label={swatchName(swatch.value)} title={swatchName(swatch.value)}
          onClick={() => { const next = variantWithValue(product, index, swatch.value, current); if (next) select(next.id); }}>
          <span style={{ backgroundColor: swatch.colour }} />
        </button>
      ))}
      {more > 0 ? <a className="xe-swatch-more" href={store?.product?.href ?? `/products/${encodeURIComponent(product.slug)}`} aria-label={`+${more}: ${product.title}`}>+{more}</a> : null}
    </div>
  );
}

// Draws a product's first non-colour choice as small links to the product on that value, one out of stock faded, or nothing when it has none to choose.
function SizeLinks({ box, product, limit }: { box: ReturnType<typeof useElementBox>; product: StorefrontProduct; limit: number }) {
  const { selected } = useProductVariant();
  const options = productOptions(product);
  const index = options.findIndex((option) => !isColourOption(option.name) && option.values.length > 1);
  if (index < 0 || product.id.startsWith('sample-')) return null;
  const current = product.variants.find((variant) => variant.id === selected);
  return (
    <div {...box} className={[box.className, 'xe-option-links'].filter(Boolean).join(' ')} role="group" aria-label={options[index].name}>
      {options[index].values.slice(0, limit).map((value) => {
        const variant = variantWithValue(product, index, value, current);
        return <a key={value} className="xe-option-link" href={`/products/${encodeURIComponent(product.slug)}${variant ? `?variant=${variant.id}` : ''}`} data-out={orderable(variant ?? undefined) ? undefined : ''}>{value}</a>;
      })}
    </div>
  );
}

// Draws the card's product's choices: sizes as links when the theme asks, pressable colours when it asks, or else plain dots.
export const SwatchesElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const { editing } = useElementRender();
  const product = useProduct();
  const said = useShopNames();
  const limit = Math.max(1, numberProp(node.props, 'limit', 7));
  if (textProp(node.props, 'showing', 'colours') === 'sizes') return product ? <SizeLinks box={box} product={product} limit={limit} /> : null;
  const found = productSwatches(product);
  const swatches = (found.length === 0 && editing && !product ? SAMPLE : found).slice(0, limit);
  if (swatches.length === 0) return null;
  if (product && boolProp(node.props, 'pickable') && !editing) return <SwatchPicker box={box} product={product} swatches={swatches} more={found.length - swatches.length} shape={choiceProp(node.props, 'shape', SHAPES, 'round')} />;
  const names = swatches.map((swatch) => swatch.value).join(', ');
  return (
    <ul {...box} className={[box.className, 'xe-swatches'].filter(Boolean).join(' ')} aria-label={named(said.colours, { names })}>
      {swatches.map((swatch) => <li key={swatch.value} className="xe-swatch" title={swatch.value} style={{ backgroundColor: swatch.colour }} />)}
    </ul>
  );
};
